import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import API_BASE_URL from '../config';

const AdminProfile = () => {
  const [authChecking, setAuthChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const fetchProfile = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const newToken = response.headers.get('X-New-Token');
      if (newToken) {
        localStorage.setItem('adminToken', newToken);
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Authentication failed');
      }

      setAdminEmail(data?.email || '');
      setIsAuthenticated(true);
      return true;
    } catch (profileError) {
      console.error('Profile fetch failed:', profileError);
      setError(profileError.message);
      setIsAuthenticated(false);
      localStorage.removeItem('adminToken');
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      setError(null);
      await fetchProfile();
      setAuthChecking(false);
    };

    checkAuth();
  }, []);

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage('');

    if (!currentPassword || !newPassword) {
      setError('Current password and new password are required');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Update failed');
      }

      setSuccessMessage(data?.message || 'Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailUpdate = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage('');

    if (!emailPassword || !newEmail) {
      setError('Password and new email are required');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/update-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: emailPassword, newEmail })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Update failed');
      }

      if (data.token) {
        localStorage.setItem('adminToken', data.token);
      }

      setSuccessMessage(data?.message || 'Email updated successfully');
      setEmailPassword('');
      setNewEmail('');
      if (data?.message) {
        setAdminEmail(newEmail);
      }
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setLoading(false);
    }
  };

  if (authChecking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please log in to access your profile.
        </Alert>
        <Button variant="contained" component={RouterLink} to="/admin">
          Go to Admin Login
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Account Email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {adminEmail || 'Not available'}
        </Typography>
        <Box component="form" onSubmit={handleEmailUpdate} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="New Email"
            type="email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            autoComplete="off"
            fullWidth
          />
          <TextField
            label="Current Password"
            type="password"
            value={emailPassword}
            onChange={(event) => setEmailPassword(event.target.value)}
            autoComplete="new-password"
            fullWidth
          />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Update Email'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Update Password
        </Typography>
        <Box component="form" onSubmit={handlePasswordUpdate} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="new-password"
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            helperText="Password must be at least 12 characters long"
            autoComplete="new-password"
            fullWidth
          />
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Update Password'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminProfile;
